import { useRef, useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ContextEditor from "@/components/ContextEditor";
import RecipeBuilder, { buildRecipeMarkdown } from "@/components/RecipeBuilder";
import { ImagePlus, ShieldCheck, Mic, ChevronLeft, ChevronRight, Save, X, Star } from "lucide-react";
import { toast } from "sonner";
import SproutBurst from "@/components/SproutBurst";
import SuggestCaptionButton from "@/components/SuggestCaptionButton";
import PolishTitleButton from "@/components/PolishTitleButton";

const CATEGORIES = ["Ideas", "Recipes", "Home", "Style", "Travel/Places", "Lifestyle", "Parenting/Family", "Wellness", "Faith", "Trusted"];
const SEASONS = [
  { value: "any", label: "Any time" },
  { value: "spring", label: "Spring" },
  { value: "summer", label: "Summer" },
  { value: "fall", label: "Fall" },
  { value: "winter", label: "Winter" },
];
const PROOF_BY_CATEGORY = {
  default: [
    "I own this",
    "I wore this",
    "I made this",
    "I visited this",
    "I've used this for 6+ months",
    "Gifted to me",
    "Paid partnership",
    "Affiliate link",
    "Idea only - I have not personally tried it",
  ],
};

// Sentence-starter chips per non-recipe category. Tapping one inserts the
// phrase into the context so the writer has something to build from instead
// of a blank box.
const SENTENCE_STARTERS = {
  Ideas:              ["I use this when…", "What I love most is…", "The one thing I'd tell a friend…", "It's not for someone who…"],
  Lifestyle:          ["I started doing this because…", "What surprised me was…", "The one thing I'd tell a friend…", "Not for you if…"],
  Faith:              ["This helped me when…", "What I've kept from it…", "Where I found this…", "It changed how I…"],
  Home:               ["I put this in…", "The change was…", "The one thing I'd tell a friend…", "It works because…"],
  Style:              ["I wear this with…", "The fit runs…", "The one thing I'd tell a friend…", "It's not for you if…"],
  "Travel/Places":    ["We went for…", "Best time to go is…", "Don't miss…", "Skip if…"],
  "Parenting/Family": ["This helped when…", "What worked was…", "The one thing I'd tell a friend…", "Skip if your kid…"],
  Wellness:           ["I do this when…", "What changed was…", "The one thing I'd tell a friend…", "Not for you if…"],
};

const DRAFT_KEY = "willoah.plant.draft.v2";
const SHOP_PREFILL_KEY = "willoah.plant.shop_prefill.v1";
const DRAFT_TTL_DAYS = 30;
const MAX_PHOTOS = 10;
// Trusted has a lighter cap — 5 feels right for a "here's the place" kind of
// post without pushing the form into full-gallery territory.
const MAX_PHOTOS_TRUSTED = 5;
const INITIAL_FORM = {
  title: "", category: "", proof_label: "", context: "", summary: "", photo_path: "",
  photos: [],
  servings: "", total_time: "", season: "any",
  provider_type: "", service_area: "", website_url: "",
  // New per-category optional fields
  room_space: "", place_location: "", scripture_ref: "",
};

export default function PlantIdea() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const brandProductId = params.get("brand_product_id");
  const [brandProduct, setBrandProduct] = useState(null);
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [recipe, setRecipe] = useState({ notes: "", ingredients: [""], steps: [""], stepTimers: [""] });
  // Non-recipe wizard step index (0 = photo, 1 = title, …). Recipes ignore this.
  const [step, setStep] = useState(0);
  const [draftFound, setDraftFound] = useState(false);
  // When arriving from a Shop This item, we prefill title + photo and stash
  // the source item's link/store/price so we can post it as a shop_item on
  // the newly-created branch. Cleared on successful submit or if the user
  // opens the plant flow fresh.
  const [shopPrefill, setShopPrefill] = useState(null);
  // Tracks the last auto-save so we can render a reassuring "Draft saved · Xs
  // ago" indicator. Kept in state (not a ref) so the child components re-render
  // as the timestamp ticks. Rebased every second by a small interval below.
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  // Celebratory sprout burst once a seed is actually planted. Holds the
  // destination path so the animation and the navigate stay on the same
  // frame — we render the burst, wait for it to finish, then push.
  const [sproutBurstFor, setSproutBurstFor] = useState(null);

  const isRecipe = form.category === "Recipes";
  const isTrusted = form.category === "Trusted";
  // Trusted uses its own compact one-screen form; recipes use the recipe
  // builder. Everything else routes through the general non-recipe wizard.
  const isWizardCategory = form.category && !isRecipe && !isTrusted;

  // Draft: restore on mount, auto-save on every change (once a category is set).
  // Drafts older than 30 days are auto-purged so localStorage doesn't accumulate
  // stale content from abandoned sessions.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      const updatedAt = d?.updated_at ? new Date(d.updated_at).getTime() : 0;
      const ageMs = Date.now() - updatedAt;
      const ttlMs = DRAFT_TTL_DAYS * 24 * 60 * 60 * 1000;
      if (!updatedAt || ageMs > ttlMs) {
        try { localStorage.removeItem(DRAFT_KEY); } catch {}
        return;
      }
      if (d?.form?.title || d?.form?.context || d?.form?.category) setDraftFound(true);
    } catch {}
  }, []);
  useEffect(() => {
    if (!form.category) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        form, recipe, step, updated_at: new Date().toISOString(),
      }));
      setLastSavedAt(Date.now());
    } catch {}
  }, [form, recipe, step]);
  // Tick every 5s so the "saved 12s ago" line stays approximately accurate
  // without wasting cycles. Only runs while a category is set (we don't
  // start saving until then).
  useEffect(() => {
    if (!form.category) return;
    const id = setInterval(() => setNowTick(Date.now()), 5000);
    return () => clearInterval(id);
  }, [form.category]);
  const savedAgoLabel = useMemo(() => {
    if (!lastSavedAt) return null;
    const s = Math.max(0, Math.floor((nowTick - lastSavedAt) / 1000));
    if (s < 3) return "Draft saved just now";
    if (s < 60) return `Draft saved · ${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `Draft saved · ${m}m ago`;
    const h = Math.floor(m / 60);
    return `Draft saved · ${h}h ago`;
  }, [lastSavedAt, nowTick]);
  const restoreDraft = () => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}");
      if (d.form) setForm({ ...INITIAL_FORM, ...d.form });
      if (d.recipe) setRecipe(d.recipe);
      if (typeof d.step === "number") setStep(d.step);
      toast("Draft restored.");
    } catch {}
    setDraftFound(false);
  };
  const discardDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
    setDraftFound(false);
  };
  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch {} };
  const saveAndExit = () => {
    toast("Saved as draft. Come back to /plant to finish.");
    navigate("/grove");
  };

  useEffect(() => {
    if (!brandProductId) return;
    api.get(`/try-and-critique/${brandProductId}`)
      .then(({ data }) => {
        setBrandProduct(data);
        setForm((prev) => ({ ...prev, category: data.category, proof_label: "Gifted to me" }));
      })
      .catch(() => toast("Could not load this Try & Critique product."));
  }, [brandProductId]);

  useEffect(() => {
    // "Turn into a full idea" prefill — from Shop This items OR gift-list
    // items. Same sessionStorage key so a burst of new sources can be added
    // without touching this effect. `from` on the querystring decides
    // whether we consume the prefill.
    const from = params.get("from");
    if (from !== "shop-item" && from !== "gift-item") return;
    try {
      const raw = sessionStorage.getItem(SHOP_PREFILL_KEY);
      if (!raw) return;
      const p = JSON.parse(raw);
      setShopPrefill(p);
      setForm((prev) => ({
        ...prev,
        title: p.title || prev.title,
        photo_path: p.photo_path || prev.photo_path,
        photos: p.photo_path ? [p.photo_path] : prev.photos,
        // If the gift-item recommend already has a comment, use it as a
        // gentle context seed — the writer can still rewrite. Only when
        // context is otherwise empty so we never trample a real draft.
        context: prev.context || (from === "gift-item" && p.recommend_hint ? p.recommend_hint : prev.context),
      }));
      if (p.photo_path) setPreview(p.photo_path);
    } catch {}
  }, [params]);

  if (!user) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-20 text-center">
        <h1 className="font-serif-display text-4xl text-[#2A2823]">Sign in to plant a seed</h1>
        <p className="text-[#2A2823]/70 mt-3">One honest idea at a time — sign in or create a free account.</p>
        <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
          <Button asChild data-testid="plant-signin-btn" className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-6 h-11 shadow-none">
            <Link to="/login">Sign in</Link>
          </Button>
          <Button asChild data-testid="plant-signup-btn" variant="outline" className="rounded-full h-11 px-6 border-[#B79256]/60 text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823] bg-transparent">
            <Link to="/signup">Create an account</Link>
          </Button>
        </div>
      </main>
    );
  }

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => {
        // Recipes: single cover only. Non-recipes: append into `photos[]`
        // and keep `photo_path` as photos[0] (the cover).
        if (prev.category === "Recipes") {
          return { ...prev, photo_path: data.path, photos: [data.path] };
        }
        const existing = prev.photos || [];
        if (existing.includes(data.path)) return prev;
        const cap = prev.category === "Trusted" ? MAX_PHOTOS_TRUSTED : MAX_PHOTOS;
        if (existing.length >= cap) {
          toast(`You can add up to ${cap} photos per seed.`);
          return prev;
        }
        const nextPhotos = [...existing, data.path];
        return { ...prev, photos: nextPhotos, photo_path: nextPhotos[0] };
      });
      toast("Photo uploaded.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Upload failed");
      setPreview(null);
    } finally {
      setUploading(false);
      // Clear the file input so re-selecting the same file re-fires onChange
      if (e.target) e.target.value = "";
    }
  };

  const removePhoto = (path) => {
    setForm((prev) => {
      const next = (prev.photos || []).filter((p) => p !== path);
      return { ...prev, photos: next, photo_path: next[0] || "" };
    });
    setPreview(null);
  };

  const makeCover = (path) => {
    setForm((prev) => {
      const rest = (prev.photos || []).filter((p) => p !== path);
      const next = [path, ...rest];
      return { ...prev, photos: next, photo_path: path };
    });
  };

  const insertStarter = (phrase) => {
    setForm((prev) => {
      const trimmed = (prev.context || "").replace(/\s+$/, "");
      const joiner = trimmed ? "\n\n" : "";
      return { ...prev, context: `${trimmed}${joiner}${phrase} ` };
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.category) return toast("Choose a category.");
    if (!isTrusted && !form.photo_path) return toast("Please upload a photo.");
    if (!form.title.trim()) return toast("Add a title.");
    if (isTrusted) {
      if (!form.provider_type.trim()) return toast("Tell us what they are, in a few words.");
      if (!form.context.trim()) return toast("Add a sentence about why you trust them.");
    } else {
      if (!form.proof_label) return toast("Choose a proof-of-use label.");
    }
    let payload = { ...form };
    // photos are only meaningful on non-recipes; strip on Recipes so the
    // backend keeps the single-cover contract intact.
    if (isRecipe) {
      delete payload.photos;
    } else {
      payload.photos = form.photos && form.photos.length ? form.photos : (form.photo_path ? [form.photo_path] : []);
    }
    if (isRecipe) {
      const md = buildRecipeMarkdown(recipe);
      if (!md.trim()) return toast("Add at least one ingredient or step.");
      payload = { ...payload, context: md };
    }
    if (!payload.context || !payload.context.trim()) return toast("Add one line of context — even a sentence works.");
    if (brandProductId) payload.brand_product_id = brandProductId;
    setBusy(true);
    try {
      const { data } = await api.post("/ideas", payload);
      clearDraft();
      // If the user came here from a Shop This item, carry that item over
      // as the first shop_item on the new branch so the retailer link
      // isn't lost. Best-effort: a failure here shouldn't block the plant.
      if (shopPrefill?.shop_item?.item_name) {
        try {
          await api.post(`/ideas/${data.id}/shop-items`, shopPrefill.shop_item);
        } catch {}
      }
      // If the flow was seeded from a Gift List item, link the new seed
      // back to that item so the owner sees "Open your seed →" next time.
      // Nothing about the list becomes public — only the seed the user
      // deliberately planted is.
      if (shopPrefill?.source === "gift-item" && shopPrefill?.gift_item_id) {
        try {
          await api.post(`/gift-items/${shopPrefill.gift_item_id}/link-planted-seed`, { idea_id: data.id });
        } catch {}
      }
      try { sessionStorage.removeItem(SHOP_PREFILL_KEY); } catch {}
      if (data.first_branch) toast("You planted your first seed on Willoah — it's a branch now. 🌱", { duration: 5000 });
      else toast("Your seed is planted — it's a branch now.");
      // Trigger the celebratory sprout, then navigate when it finishes.
      // The burst renders in a portal so it survives navigation, but we
      // keep the transition tight (~1.1s) so people don't feel held back.
      setSproutBurstFor(`/idea/${data.id}`);
    } catch (err) {
      // Surface the ACTUAL failure so submitters aren't stuck staring at
      // "Could not plant idea." with no way forward. FastAPI can return three
      // shapes: (a) HTTPException → detail is a string, (b) Pydantic 422 →
      // detail is an array of {loc, msg, type}, (c) network / CORS failure →
      // no response at all. Handle each explicitly.
      // eslint-disable-next-line no-console
      console.error("Plant idea failed", err?.response?.status, err?.response?.data, err);
      const detail = err?.response?.data?.detail;
      let msg;
      if (!err?.response) {
        msg = "Couldn't reach the server. Check your connection and try again.";
      } else if (err.response.status === 401) {
        // Check status BEFORE string-detail so an expired session shows a
        // friendly message instead of the raw backend "Not authenticated".
        msg = "Your session expired. Sign in again to plant this idea.";
      } else if (err.response.status === 413) {
        msg = "Photo is too big (max 8MB). Try a smaller image.";
      } else if (typeof detail === "string" && detail.trim()) {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        // Pydantic validation: build a friendly "field: message" list.
        msg = detail
          .map((d) => {
            const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
            const m = d?.msg || "invalid";
            return field ? `${field}: ${m}` : m;
          })
          .join(" · ");
      } else if (err.response.status >= 500) {
        msg = "Something broke on our end. Please try again in a moment.";
      } else {
        msg = `Could not plant idea (status ${err.response.status}).`;
      }
      toast(msg);
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-3xl mx-auto px-6 pt-12 pb-24">
      <SproutBurst
        open={!!sproutBurstFor}
        onDone={() => {
          if (sproutBurstFor) {
            const dest = sproutBurstFor;
            setSproutBurstFor(null);
            navigate(dest);
          }
        }}
      />
      <span className="accent-label text-[#B79256]">a new seed</span>
      <h1 className="font-serif-display text-5xl text-[#2A2823] mt-3">Plant a seed</h1>
      <p className="text-[#2A2823]/60 mt-3">One honest thing at a time.</p>

      {savedAgoLabel && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#4E5A3D]/8 border border-[#4E5A3D]/25 pl-3 pr-4 py-1.5 text-[11px] text-[#4E5A3D]">
          <Save className="w-3 h-3" strokeWidth={2} />
          <span className="accent-label" data-testid="draft-saved-indicator">{savedAgoLabel}</span>
          <span className="text-[#2A2823]/40 italic">· come back any time on this device</span>
        </div>
      )}

      {shopPrefill && (
        <div
          data-testid="shop-prefill-banner"
          className="mt-6 rounded-2xl border border-[#B79256]/40 bg-[#B79256]/12 px-5 py-4 flex items-start justify-between gap-3 flex-wrap"
        >
          <div className="text-sm text-[#2A2823]/85 leading-relaxed">
            <b className="font-serif-display text-lg text-[#8B6E3F] block leading-tight">
              {shopPrefill.source === "gift-item"
                ? "Prefilled from your Gift List — only the seed becomes public"
                : "Prefilled from your Shop This item"}
            </b>
            <span className="italic text-[#2A2823]/70">"{shopPrefill.title}"</span>
            {shopPrefill.shop_item?.url && <span className="text-[#2A2823]/50"> · {new URL(shopPrefill.shop_item.url).hostname.replace(/^www\./, "")}</span>}
            <div className="text-xs text-[#2A2823]/60 mt-1">
              Just add the missing pieces — category, proof of use, and context. The link comes along automatically.
            </div>
          </div>
          <button
            type="button"
            data-testid="shop-prefill-discard"
            onClick={() => {
              try { sessionStorage.removeItem(SHOP_PREFILL_KEY); } catch {}
              setShopPrefill(null);
              setForm(INITIAL_FORM);
              setPreview(null);
              toast("Cleared — starting fresh.");
            }}
            className="text-xs text-[#8B6E3F] hover:text-[#6b5430] underline underline-offset-2"
          >
            start fresh instead
          </button>
        </div>
      )}

      {draftFound && (
        <div data-testid="draft-restore-banner" className="mt-6 rounded-2xl border border-[#4E5A3D]/40 bg-[#4E5A3D]/8 p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Save className="w-4 h-4 text-[#4E5A3D]" strokeWidth={2} />
            <span className="text-sm text-[#2A2823]/85">You have an unfinished seed. Pick up where you left off?</span>
          </div>
          <div className="flex gap-2">
            <button type="button" data-testid="draft-discard" onClick={discardDraft} className="text-xs text-[#2A2823]/55 hover:text-[#2A2823] px-2">Start fresh</button>
            <Button data-testid="draft-restore" onClick={restoreDraft} className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-9 px-4 shadow-none text-xs">Restore</Button>
          </div>
        </div>
      )}

      {brandProduct && (
        <div data-testid="tc-locked-banner" className="mt-6 rounded-2xl border border-[#4E5A3D]/30 bg-[#4E5A3D]/6 p-5 flex gap-3">
          <ShieldCheck className="w-5 h-5 text-[#4E5A3D] flex-shrink-0 mt-0.5" strokeWidth={1.75}/>
          <div>
            <div className="accent-label text-[#4E5A3D]">try &amp; critique review</div>
            <p className="font-serif-display text-2xl text-[#2A2823] leading-tight mt-1">
              Reviewing <em className="not-italic text-[#4E5A3D]">{brandProduct.brand_name} — {brandProduct.product_name}</em>
            </p>
          </div>
        </div>
      )}

      {/* Step 0: pick a category (both flows share this) */}
      {!form.category && (
        <section className="mt-10" data-testid="category-picker">
          <Label className="accent-label text-[#B79256]">What kind of thing?</Label>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`category-tile-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => { setForm((f) => ({ ...f, category: c })); setStep(0); }}
                className="rounded-2xl border border-[#B79256]/40 bg-white/60 hover:bg-[#B79256]/8 hover:border-[#4E5A3D] transition-colors py-6 font-serif-display text-lg text-[#2A2823]"
              >
                {c}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Recipes: unchanged all-at-once form */}
      {isRecipe && <RecipeForm
        form={form} setForm={setForm} recipe={recipe} setRecipe={setRecipe}
        preview={preview} uploading={uploading} busy={busy}
        fileRef={fileRef} onPickFile={onPickFile} submit={submit}
        brandProduct={brandProduct}
      />}

      {/* Trusted: light, guided one-screen form — not the multi-step wizard.
          Trusted is for local people/places/services someone would personally
          recommend; the form stays minimal so the barrier to entry is low. */}
      {isTrusted && <TrustedForm
        form={form} setForm={setForm}
        preview={preview} uploading={uploading} busy={busy}
        fileRef={fileRef} onPickFile={onPickFile} submit={submit}
        removePhoto={removePhoto} makeCover={makeCover}
      />}

      {/* Ideas / Lifestyle / Faith / Home / Style / Travel / Parenting / Wellness: one-step-at-a-time wizard */}
      {isWizardCategory && <NonRecipeWizard
        form={form} setForm={setForm}
        preview={preview} setPreview={setPreview} uploading={uploading}
        fileRef={fileRef} onPickFile={onPickFile}
        removePhoto={removePhoto} makeCover={makeCover}
        step={step} setStep={setStep}
        insertStarter={insertStarter}
        busy={busy} submit={submit}
        brandProduct={brandProduct}
        saveAndExit={saveAndExit}
      />}
    </main>
  );
}


// ---------- Non-Recipe Wizard (Ideas / Lifestyle / Faith / Home / Style / Travel / Parenting / Wellness) ----------

// ---------- Trusted (light guided form) ----------
// A single-screen form for local people, places, and services the sharer would
// personally recommend. No step-by-step wizard, no proof-of-use label picker,
// no photo requirement — just four small fields that guide the writer without
// getting in their way. The explainer at the top of the form is what teaches
// visitors what Trusted is for.
function TrustedForm({ form, setForm, preview, uploading, busy, fileRef, onPickFile, submit, removePhoto, makeCover }) {
  const photos = form.photos || [];
  return (
    <section className="mt-8" data-testid="trusted-form">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <button
          type="button"
          onClick={() => setForm((f) => ({ ...f, category: "" }))}
          className="accent-label text-xs text-[#2A2823]/55 hover:text-[#2A2823]"
          data-testid="trusted-change-category"
        >
          ← change category
        </button>
        <span className="accent-label text-xs text-[#2A2823]/45">Trusted</span>
      </div>

      <div
        data-testid="trusted-explainer"
        className="rounded-2xl bg-[#4E5A3D]/8 border border-[#4E5A3D]/25 px-5 py-4 mb-6"
      >
        <p className="text-sm text-[#2A2823] leading-relaxed">
          <b className="font-serif-display text-base text-[#4E5A3D]">Trusted</b> — local people
          and places you'd personally recommend: a dentist, a restaurant, a daycare, a contractor,
          a tutor, a favourite museum. Anyone worth telling a friend about.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="trusted-form-fields">
        {/* 1 · Name */}
        <div>
          <Label className="accent-label text-[#B79256]">Name of place, business, or person</Label>
          <Input
            data-testid="trusted-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Dr. Rivera · Frothy Monkey · Emma the Piano Tutor"
            className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11 font-serif-display text-lg"
            maxLength={120}
            required
          />
        </div>

        {/* 2 · What is it */}
        <div>
          <Label className="accent-label text-[#B79256]">What is it, in a few words?</Label>
          <Input
            data-testid="trusted-provider-type-input"
            value={form.provider_type}
            onChange={(e) => setForm({ ...form, provider_type: e.target.value })}
            placeholder="Pediatric dentist · Coffee shop in East Nashville · Piano tutor for kids"
            className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"
            maxLength={120}
          />
        </div>

        {/* 3 · Why do you trust them — the main content */}
        <div>
          <Label className="accent-label text-[#B79256]">Why do you trust them?</Label>
          <p className="text-xs text-[#2A2823]/55 mt-1">Speak plainly — what makes them worth recommending?</p>
          <textarea
            data-testid="trusted-context-input"
            value={form.context}
            onChange={(e) => setForm({ ...form, context: e.target.value })}
            placeholder="They've never oversold us on treatment. My kids actually look forward to going. Front-desk always calls back same day."
            rows={5}
            className="mt-2 w-full rounded-xl border border-[#B79256]/40 bg-[#FBF8F1] focus-visible:border-[#4E5A3D] focus-visible:ring-[#4E5A3D] focus-visible:ring-1 outline-none px-3 py-2 text-sm leading-relaxed resize-y"
            required
          />
        </div>

        {/* 4 · Location */}
        <div>
          <Label className="accent-label text-[#B79256]">Location / City</Label>
          <Input
            data-testid="trusted-service-area-input"
            value={form.service_area}
            onChange={(e) => setForm({ ...form, service_area: e.target.value })}
            placeholder="Nashville, TN"
            className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"
            maxLength={120}
          />
        </div>

        {/* Photos — optional. Up to 5. Trusted intentionally doesn't require
            any so people can post from memory without hunting for a picture.
            First photo is the cover; tap ★ on another to promote it. */}
        <div>
          <Label className="accent-label text-[#B79256]">
            Photos <span className="text-[#2A2823]/50 font-normal ml-1">· optional</span>
            {photos.length > 0 && (
              <span className="text-[#2A2823]/50 font-normal ml-2" data-testid="trusted-photos-count">
                · {photos.length} of {MAX_PHOTOS_TRUSTED}
              </span>
            )}
          </Label>
          {photos.length === 0 ? (
            <button
              type="button"
              data-testid="trusted-photo-add"
              onClick={() => fileRef.current?.click()}
              className="mt-3 w-full sm:w-auto inline-flex items-center gap-2 rounded-xl border border-dashed border-[#B79256]/50 bg-white/60 hover:bg-[#B79256]/8 px-4 py-3 text-sm text-[#2A2823]/70"
              disabled={uploading}
            >
              <ImagePlus className="w-4 h-4 text-[#B79256]" strokeWidth={1.75} />
              {uploading ? "Uploading…" : "Add a photo (optional)"}
            </button>
          ) : (
            <div
              data-testid="trusted-photos-gallery"
              className="mt-3 grid gap-3 grid-cols-2 sm:grid-cols-3"
            >
              {photos.map((p, idx) => (
                <div
                  key={p}
                  data-testid={`trusted-photo-tile-${idx}`}
                  className={`relative rounded-2xl overflow-hidden border-2 bg-white/60 aspect-[4/3] ${idx === 0 ? "border-[#4E5A3D]" : "border-[#B79256]/30"}`}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                    alt={`angle ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {idx === 0 && (
                    <span
                      data-testid={`trusted-photo-cover-badge-${idx}`}
                      className="absolute top-1.5 left-1.5 accent-label text-[10px] bg-[#4E5A3D] text-[#FBF8F1] px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    >
                      <Star className="w-2.5 h-2.5" strokeWidth={2.5} /> cover
                    </span>
                  )}
                  <div className="absolute top-1.5 right-1.5 flex gap-1">
                    {idx !== 0 && (
                      <button
                        type="button"
                        data-testid={`trusted-photo-make-cover-${idx}`}
                        onClick={() => makeCover(p)}
                        title="Make cover"
                        className="w-6 h-6 rounded-full bg-white/85 hover:bg-white text-[#4E5A3D] flex items-center justify-center shadow-sm"
                      >
                        <Star className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`trusted-photo-remove-${idx}`}
                      onClick={() => removePhoto(p)}
                      title="Remove"
                      className="w-6 h-6 rounded-full bg-white/85 hover:bg-[#7A5A2E] hover:text-[#FBF8F1] text-[#2A2823] flex items-center justify-center shadow-sm"
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </div>
                </div>
              ))}
              {photos.length < MAX_PHOTOS_TRUSTED && (
                <button
                  type="button"
                  data-testid="trusted-photo-add-more"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="cursor-pointer rounded-2xl border-2 border-dashed border-[#B79256]/50 bg-white/40 hover:bg-[#B79256]/8 transition-colors flex flex-col items-center justify-center text-[#2A2823]/60 aspect-[4/3]"
                >
                  <ImagePlus className="w-6 h-6 text-[#B79256]" strokeWidth={1.5} />
                  <span className="accent-label text-xs mt-2">
                    {uploading ? "Uploading…" : "+ Add another"}
                  </span>
                </button>
              )}
            </div>
          )}
          <input data-testid="trusted-photo-input" ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" />
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            data-testid="trusted-submit"
            disabled={busy}
            className="rounded-full h-11 px-6 bg-[#4E5A3D] hover:bg-[#3E4830] text-[#FBF8F1] shadow-none"
          >
            {busy ? "Planting…" : "Plant this seed"}
          </Button>
        </div>
      </form>
    </section>
  );
}

// ---------- Non-Recipe Wizard ----------

function NonRecipeWizard({
  form, setForm, preview, setPreview, uploading, fileRef, onPickFile,
  removePhoto, makeCover,
  step, setStep, insertStarter, busy, submit, brandProduct, saveAndExit,
}) {
  const photos = form.photos || [];
  // Categories with a single optional "extras" field (room / place / scripture).
  // We surface one extra step so the wizard stays tap-tap-tap, never crowded.
  const extrasField = useMemo(() => {
    if (form.category === "Home") return "room_space";
    if (form.category === "Travel/Places") return "place_location";
    if (form.category === "Faith") return "scripture_ref";
    return null;
  }, [form.category]);
  // Steps: photo, title, proof, [extras], context
  const stepDefs = useMemo(() => {
    const list = ["photo", "title", "proof"];
    if (extrasField) list.push("extras");
    list.push("context");
    return list;
  }, [extrasField]);
  const stepName = stepDefs[step] || "context";
  const totalSteps = stepDefs.length;

  const canAdvance = () => {
    switch (stepName) {
      case "photo": return photos.length > 0;
      case "title": return !!form.title.trim();
      case "proof": return !!form.proof_label;
      case "extras": return true; // extras are optional — always allow forward
      case "context": return !!form.context.trim();
      default: return true;
    }
  };
  const next = () => { if (!canAdvance()) return toast("Fill this in first."); setStep((s) => Math.min(s + 1, totalSteps - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const proofOptions = PROOF_BY_CATEGORY.default;
  const starters = SENTENCE_STARTERS[form.category] || [];

  return (
    <section className="mt-10" data-testid={`wizard-step-${stepName}`}>
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <button
          type="button"
          onClick={() => setForm((f) => ({ ...f, category: "" }))}
          className="accent-label text-xs text-[#2A2823]/55 hover:text-[#2A2823]"
          data-testid="wizard-change-category"
        >
          ← change category
        </button>
        <span className="accent-label text-xs text-[#2A2823]/45" data-testid="wizard-progress">
          {form.category} · step {step + 1} of {totalSteps}
        </span>
      </div>

      {/* Photo — up to 10 tiles; first is cover */}
      {stepName === "photo" && (
        <div>
          <Label className="accent-label text-[#B79256]">
            Photo
            {photos.length > 0 && (
              <span className="text-[#2A2823]/50 font-normal ml-2" data-testid="photos-count">
                · {photos.length} of {MAX_PHOTOS}
              </span>
            )}
          </Label>
          {photos.length === 0 ? (
            <div
              data-testid="upload-dropzone"
              onClick={() => fileRef.current?.click()}
              className="mt-3 cursor-pointer rounded-3xl border-2 border-dashed border-[#B79256]/50 bg-white/60 flex items-center justify-center overflow-hidden hover:bg-[#B79256]/8 transition-colors aspect-[4/3]"
            >
              <div className="text-center text-[#2A2823]/60 px-6">
                <ImagePlus className="w-8 h-8 mx-auto mb-3 text-[#B79256]" strokeWidth={1.5} />
                <p className="font-serif-display text-xl text-[#2A2823]">
                  {uploading ? "Uploading…" : "Upload a photo"}
                </p>
                <p className="text-xs mt-2 accent-label text-[#2A2823]/50">
                  jpg or png · up to 8mb
                </p>
              </div>
            </div>
          ) : (
            <>
              <div
                data-testid="photos-gallery"
                className="mt-3 grid gap-3 grid-cols-2 sm:grid-cols-3"
              >
                {photos.map((p, idx) => (
                  <div
                    key={p}
                    data-testid={`photo-tile-${idx}`}
                    className={`relative rounded-2xl overflow-hidden border-2 bg-white/60 aspect-square ${idx === 0 ? "border-[#4E5A3D]" : "border-[#B79256]/30"}`}
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                      alt={`angle ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {idx === 0 && (
                      <span
                        data-testid={`photo-cover-badge-${idx}`}
                        className="absolute top-1.5 left-1.5 accent-label text-[10px] bg-[#4E5A3D] text-[#FBF8F1] px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                      >
                        <Star className="w-2.5 h-2.5" strokeWidth={2.5} /> cover
                      </span>
                    )}
                    <div className="absolute top-1.5 right-1.5 flex gap-1">
                      {idx !== 0 && (
                        <button
                          type="button"
                          data-testid={`photo-make-cover-${idx}`}
                          onClick={() => makeCover(p)}
                          title="Make cover"
                          className="w-6 h-6 rounded-full bg-white/85 hover:bg-white text-[#4E5A3D] flex items-center justify-center shadow-sm"
                        >
                          <Star className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid={`photo-remove-${idx}`}
                        onClick={() => removePhoto(p)}
                        title="Remove"
                        className="w-6 h-6 rounded-full bg-white/85 hover:bg-[#7A5A2E] hover:text-[#FBF8F1] text-[#2A2823] flex items-center justify-center shadow-sm"
                      >
                        <X className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <button
                    type="button"
                    data-testid="photo-add-more"
                    onClick={() => fileRef.current?.click()}
                    className="cursor-pointer rounded-2xl border-2 border-dashed border-[#B79256]/50 bg-white/40 hover:bg-[#B79256]/8 transition-colors flex flex-col items-center justify-center text-[#2A2823]/60 aspect-square"
                    disabled={uploading}
                  >
                    <ImagePlus className="w-6 h-6 text-[#B79256]" strokeWidth={1.5} />
                    <span className="accent-label text-xs mt-2">
                      {uploading ? "Uploading…" : "+ Add another angle"}
                    </span>
                  </button>
                )}
              </div>
              <p className="mt-3 text-xs text-[#2A2823]/55">
                First photo is the cover — the one that shows on cards and shares. Tap ★ on another to promote it.
              </p>
            </>
          )}
          <input data-testid="upload-input" ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" />
        </div>
      )}

      {/* Title */}
      {stepName === "title" && (
        <div>
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <Label className="accent-label text-[#B79256]">Title</Label>
            <PolishTitleButton
              draft={form}
              onPick={(t) => setForm({ ...form, title: t })}
              testid="polish-title-wizard"
            />
          </div>
          <Input
            data-testid="idea-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="What is it?"
            className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] h-11 font-serif-display text-lg"
            autoFocus
          />
          <VoiceHint testid="voice-hint-title" />
        </div>
      )}

      {/* Proof */}
      {stepName === "proof" && (
        <div>
          <Label className="accent-label text-[#B79256]">How do you actually know it?</Label>
          <div className="mt-4 grid gap-2">
            {proofOptions.map((p) => (
              <button
                key={p}
                type="button"
                data-testid={`proof-tile-${p.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setForm({ ...form, proof_label: p })}
                className={`text-left rounded-xl px-4 py-3 border transition-colors ${
                  form.proof_label === p
                    ? "bg-[#4E5A3D] text-[#FBF8F1] border-[#4E5A3D]"
                    : "bg-white/60 hover:bg-[#B79256]/8 border-[#B79256]/40 text-[#2A2823]"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category-specific optional extras — never required, tap Next to skip. */}
      {stepName === "extras" && extrasField === "room_space" && (
        <div data-testid="extras-room-space">
          <Label className="accent-label text-[#B79256]">Room or space <span className="text-[#2A2823]/50">· optional</span></Label>
          <p className="text-xs text-[#2A2823]/55 mt-1">Which part of the home is this about?</p>
          <Input
            data-testid="room-space-input"
            value={form.room_space}
            onChange={(e) => setForm({ ...form, room_space: e.target.value })}
            placeholder="e.g. kitchen, mudroom, kids' room, front porch"
            className="mt-3 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"
            maxLength={80}
          />
        </div>
      )}
      {stepName === "extras" && extrasField === "place_location" && (
        <div data-testid="extras-place-location">
          <Label className="accent-label text-[#B79256]">Where is it? <span className="text-[#2A2823]/50">· optional</span></Label>
          <p className="text-xs text-[#2A2823]/55 mt-1">A friendly location tag — city, region, or neighborhood.</p>
          <Input
            data-testid="place-location-input"
            value={form.place_location}
            onChange={(e) => setForm({ ...form, place_location: e.target.value })}
            placeholder="e.g. Nashville, TN · Big Sur, CA · Provence, France"
            className="mt-3 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"
            maxLength={120}
          />
        </div>
      )}
      {stepName === "extras" && extrasField === "scripture_ref" && (
        <div data-testid="extras-scripture-ref">
          <Label className="accent-label text-[#B79256]">Scripture reference <span className="text-[#2A2823]/50">· optional</span></Label>
          <p className="text-xs text-[#2A2823]/55 mt-1">A verse or passage tied to this seed, if any.</p>
          <Input
            data-testid="scripture-ref-input"
            value={form.scripture_ref}
            onChange={(e) => setForm({ ...form, scripture_ref: e.target.value })}
            placeholder="e.g. Psalm 23:1 · Matthew 6:33 · Romans 8"
            className="mt-3 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"
            maxLength={120}
          />
        </div>
      )}

      {/* Context with sentence-starter chips + voice hint */}
      {stepName === "context" && (
        <div>
          <Label className="accent-label text-[#B79256]">One line is enough</Label>
          <p className="text-xs text-[#2A2823]/55 mt-1">Tap a starter to get going — you can rewrite over it.</p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="sentence-starters">
            {starters.map((phrase) => (
              <button
                key={phrase}
                type="button"
                data-testid={`starter-${phrase.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}
                onClick={() => insertStarter(phrase)}
                className="text-xs rounded-full bg-white/60 border border-[#B79256]/40 hover:bg-[#B79256]/10 hover:border-[#4E5A3D] px-3 py-1.5 text-[#2A2823]/85 transition-colors"
              >
                {phrase}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <ContextEditor
              value={form.context}
              onChange={(v) => setForm({ ...form, context: v })}
              placeholder="Just say the honest thing you'd tell a friend."
            />
          </div>
          <VoiceHint testid="voice-hint-context" />

          {/* Optional short caption for the feed card. Especially useful
              for recipes whose Story starts with an ingredient list — the
              card would otherwise show truncated `## Ingredients · 1 c
              flour…`. If empty, the card falls back to the first prose
              paragraph of the Story. Cap at 160 chars, one line. */}
          <div className="mt-6">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <Label className="accent-label text-[#B79256]">
                Card caption <span className="text-[#2A2823]/50 font-normal ml-1">· optional</span>
              </Label>
              <SuggestCaptionButton
                draft={form}
                onSuggest={(caption) => setForm({ ...form, summary: caption })}
                testid="suggest-caption-wizard"
              />
            </div>
            <p className="text-xs text-[#2A2823]/55 mt-1">
              One sentence that shows on the feed card. Skip it and we'll pick a line from your Story.
            </p>
            <Input
              data-testid="idea-summary-input"
              value={form.summary || ""}
              onChange={(e) => setForm({ ...form, summary: e.target.value.slice(0, 160) })}
              placeholder={form.category === "Recipes" ? "e.g. Fast weeknight dinner everyone eats." : "e.g. The pillow every guest asks about."}
              maxLength={160}
              className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"
            />
            <p className="text-[11px] text-[#2A2823]/40 mt-1 text-right">{(form.summary || "").length}/160</p>
          </div>

          <p className="text-xs text-[#2A2823]/50 mt-4">
            You can add Field Notes and Shop This items later, from the seed page.
          </p>
        </div>
      )}

      {/* Nav footer */}
      <div className="mt-10 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={back}
          disabled={step === 0}
          data-testid="wizard-back"
          className="inline-flex items-center gap-1 text-sm text-[#2A2823]/60 hover:text-[#2A2823] disabled:opacity-30"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={2} /> Back
        </button>
        <button
          type="button"
          onClick={saveAndExit}
          data-testid="wizard-save-exit"
          className="accent-label text-xs text-[#4E5A3D] hover:text-[#3E4830]"
        >
          Save as draft
        </button>
        {step < totalSteps - 1 ? (
          <Button
            type="button"
            onClick={next}
            data-testid="wizard-next"
            className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-11 px-6 shadow-none"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" strokeWidth={2} />
          </Button>
        ) : (
          <Button
            type="button"
            data-testid="plant-submit-btn"
            onClick={submit}
            disabled={busy || uploading}
            className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-12 px-7 shadow-none"
          >
            {busy ? "Planting…" : "Plant this idea"}
          </Button>
        )}
      </div>
    </section>
  );
}


function VoiceHint({ testid }) {
  return (
    <div data-testid={testid} className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#2A2823]/55">
      <Mic className="w-3 h-3 text-[#B79256]" strokeWidth={2} />
      <span>or just talk it out — most phones have voice-to-text on the keyboard</span>
    </div>
  );
}


// ---------- Recipes: unchanged all-at-once form ----------

function RecipeForm({
  form, setForm, recipe, setRecipe, preview, uploading, busy,
  fileRef, onPickFile, submit, brandProduct,
}) {
  return (
    <form onSubmit={submit} className="mt-10 space-y-8" data-testid="plant-form">
      <div>
        <Label className="accent-label text-[#B79256]">Photo</Label>
        <div
          data-testid="upload-dropzone"
          onClick={() => fileRef.current?.click()}
          className="mt-3 cursor-pointer rounded-3xl border-2 border-dashed border-[#B79256]/50 bg-white/60 aspect-[4/3] flex items-center justify-center overflow-hidden hover:bg-[#B79256]/8 transition-colors"
        >
          {preview ? (
            <img src={preview} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <div className="text-center text-[#2A2823]/60">
              <ImagePlus className="w-8 h-8 mx-auto mb-3 text-[#B79256]" strokeWidth={1.5} />
              <p className="font-serif-display text-xl text-[#2A2823]">{uploading ? "Uploading…" : "Upload a photo"}</p>
              <p className="text-xs mt-2 accent-label text-[#2A2823]/50">jpg or png · up to 8mb</p>
            </div>
          )}
        </div>
        <input data-testid="upload-input" ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" />
      </div>

      <div>
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <Label htmlFor="title" className="accent-label text-[#B79256]">Title</Label>
          <PolishTitleButton
            draft={{
              title: form.title,
              category: form.category || "Recipes",
              context: `${form.title}\n\n${recipe.notes || ""}\n\nIngredients:\n${(recipe.ingredients || []).filter(Boolean).join("\n")}`,
            }}
            onPick={(t) => setForm({ ...form, title: t })}
            testid="polish-title-recipe"
          />
        </div>
        <Input
          id="title"
          data-testid="idea-title-input"
          required
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="What is it?"
          className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] h-11 font-serif-display text-lg"
        />
      </div>

      <div>
        <Label className="accent-label text-[#B79256]">Proof of use {brandProduct && <span className="text-[#4E5A3D]">· locked</span>}</Label>
        <Select value={form.proof_label} onValueChange={(v) => setForm({ ...form, proof_label: v })} disabled={!!brandProduct}>
          <SelectTrigger data-testid="proof-select" className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11 disabled:opacity-100 disabled:cursor-not-allowed">
            <SelectValue placeholder="How do you know it?" />
          </SelectTrigger>
          <SelectContent className="bg-[#FBF8F1] border-[#B79256]/40">
            {PROOF_BY_CATEGORY.default.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div data-testid="recipe-fields" className="grid sm:grid-cols-3 gap-6 rounded-2xl border border-[#B79256]/30 bg-white/60 p-5">
        <div>
          <Label className="accent-label text-[#B79256]">Servings</Label>
          <Input data-testid="servings-input" value={form.servings} onChange={(e) => setForm({ ...form, servings: e.target.value })} placeholder="e.g. 4" className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11" />
        </div>
        <div>
          <Label className="accent-label text-[#B79256]">Total time</Label>
          <Input data-testid="total-time-input" value={form.total_time} onChange={(e) => setForm({ ...form, total_time: e.target.value })} placeholder="e.g. 45 min" className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11" />
        </div>
        <div>
          <Label className="accent-label text-[#B79256]">Best season</Label>
          <Select value={form.season} onValueChange={(v) => setForm({ ...form, season: v })}>
            <SelectTrigger data-testid="season-select" className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11">
              <SelectValue placeholder="Any time" />
            </SelectTrigger>
            <SelectContent className="bg-[#FBF8F1] border-[#B79256]/40">
              {SEASONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="sm:col-span-3 text-xs text-[#2A2823]/50 -mt-2 leading-relaxed">
          Season is used to surface saved recipes on Your Grove when they feel right for right now.
        </p>
      </div>

      <div>
        <Label className="accent-label text-[#B79256]">Recipe</Label>
        <div className="mt-2">
          <RecipeBuilder
            notes={recipe.notes}
            ingredients={recipe.ingredients}
            steps={recipe.steps}
            stepTimers={recipe.stepTimers}
            onChange={setRecipe}
          />
        </div>
      </div>

      {/* Optional short caption — solves the "card shows the raw ingredient
          list" problem. Empty falls back to the first prose line in the
          Story markdown. */}
      <div>
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <Label className="accent-label text-[#B79256]">
            Card caption <span className="text-[#2A2823]/50 font-normal ml-1">· optional</span>
          </Label>
          <SuggestCaptionButton
            draft={{
              title: form.title,
              category: form.category || "Recipes",
              // Recipe context is generated at submit; include the current
              // typed notes + ingredient/step lists so the caption reflects
              // what the author has entered so far.
              context: `${form.title}\n\n${recipe.notes || ""}\n\nIngredients:\n${(recipe.ingredients || []).filter(Boolean).join("\n")}`,
            }}
            onSuggest={(caption) => setForm({ ...form, summary: caption })}
            testid="suggest-caption-recipe"
          />
        </div>
        <p className="text-xs text-[#2A2823]/55 mt-1">
          One sentence that shows on the feed card. Skip it and we'll pull a line from your Story.
        </p>
        <Input
          data-testid="idea-summary-input-recipe"
          value={form.summary || ""}
          onChange={(e) => setForm({ ...form, summary: e.target.value.slice(0, 160) })}
          placeholder="e.g. Fast weeknight dinner everyone eats."
          maxLength={160}
          className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1] h-11"
        />
        <p className="text-[11px] text-[#2A2823]/40 mt-1 text-right">{(form.summary || "").length}/160</p>
      </div>

      <Button
        data-testid="plant-submit-btn"
        type="submit"
        disabled={busy || uploading}
        className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-7 h-12 text-base shadow-none"
      >
        {busy ? "Planting…" : "Plant this idea"}
      </Button>
    </form>
  );
}
